 
import type { Type } from '@angular/core';
import type { ActivatedRouteSnapshot, CanActivateFn, RouterStateSnapshot, UrlCreationOptions } from '@angular/router';

import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { RedirectCommand, Router } from '@angular/router';
import { ELIBOO_ERROR_CODE, ElibooError } from '@eliboo/shared/common';
import { catchError, map, of, switchMap, tap, timer } from 'rxjs';

import type { BaseUserService } from '../services/base-user.service';
export interface BaseRouteParams {
  _encryptCode?: string;
  _loginType: string;
  _token?: string;
}
export function getUserAuthGuardFn(
  userServiceClass: Type<BaseUserService<unknown>>,
  getRedirectParamsFn: (
    injector: Injector,
    errMsg: string,
    rawError: unknown,
  ) => {
    commands: string[];
    navigationExtras?: UrlCreationOptions;
  },
  // 强制通过模式：尝试登录/初始化，失败不影响进入页面（用于免登录浏览场景，如完整商城公开段）
  forcePass = false,
) {
  const taxAppCanActivateFn: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const router = inject(Router);
    const injector = inject(Injector);
    const userSrv = inject(userServiceClass);
    const params = route.queryParams as BaseRouteParams;
    return of(null).pipe(
      switchMap(() => (params._loginType ? loginInit(userSrv, params) : commonInit(userSrv, userSrv.configs.fasterInit))),
      map(() => true),
      catchError((err) => {
        // 强制通过模式：登录失败不影响进入
        if (forcePass) {
          console.warn('getUserAuthGuardFn.forcePass login failed, continue entering', err);
          return of(true);
        }
        let errMsg = '';
        if (err instanceof ElibooError) {
          errMsg = err.message;
          console.error('taxInitResolver.ElibooError', err, err.cause);
        } else if (err instanceof Error) {
          errMsg = err.message;
          console.error('taxInitResolver.Error', err);
        } else if (err instanceof HttpErrorResponse) {
          const error = err.error;
          errMsg = error.msg ?? error.error_description ?? err.message;
          console.error('taxInitResolver.HttpErrorResponse', err);
        } else {
          console.error('taxInitResolver.UnknownError', err);
          errMsg = err.toString();
        }
        const { commands, navigationExtras } = getRedirectParamsFn(injector, errMsg, err);
        return of(new RedirectCommand(router.createUrlTree(commands, navigationExtras)));
      }),
    );
  };
  return taxAppCanActivateFn;
}

function commonInit(userSrv: BaseUserService<unknown>, fasterInit = false) {
  const tokenSrv = userSrv.tokenSrv;
  if (tokenSrv.isExpired()) {
    tokenSrv.clear(); // TODO 判断是否可以刷新token
    throw new ElibooError('token-expired', ELIBOO_ERROR_CODE['token-expired']);
  }
  const localUser = userSrv.user();
  return fasterInit && localUser ? of(localUser) : userSrv.refreshUserInfo().pipe(map((res) => res.data));
}
function loginInit(userSrv: BaseUserService<unknown>, queryParams: BaseRouteParams) {
  const tokenSrv = userSrv.tokenSrv;
  if (tokenSrv.isExpired()) userSrv.loginOut();
  // 检测本地用户(_creditCode)和路径上是否一致
  if (userSrv.checkIsSameUser(queryParams)) {
    // 用户一致， 异步登录、快速初始化
    return commonInit(userSrv, true).pipe(
      tap(() => {
        // 快速登录成功，3秒后异步刷新token
        timer(1000 * 3)
          .pipe(switchMap(() => userSrv.doLoginByParams(queryParams, true)))
          .subscribe();
      }),
      catchError(() => {
        // 快速登录失败，同步登录
        return userSrv.doLoginByParams(queryParams, false).pipe(map((res) => res.userRes));
      }),
    );
  } else {
    // 不一致，同步登录
    return userSrv.doLoginByParams(queryParams, false).pipe(map((res) => res.userRes));
  }
}
